import { Newspaper } from 'lucide-react';
import { DEFAULT_TAG_HOURS, gistOf, type GistContent } from '@london-life/contracts';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { NumberField, type NumberSpec } from './form';

type GistNumbers = Exclude<keyof GistContent, 'visible'>;
const whole = (key: GistNumbers, label: string, min: number, max: number): NumberSpec<GistNumbers> => ({
  key,
  label,
  unit: 'number',
  min,
  max,
  whole: true,
});
const weight = (key: GistNumbers, label: string): NumberSpec<GistNumbers> => ({
  key,
  label,
  unit: 'number',
  min: 0,
  max: 100,
});
const RULES: NumberSpec<GistNumbers>[] = [
  whole('trendingHours', 'Trending looks back this many hours', 1, 336),
  weight('likeWeight', 'Trending: a like counts'),
  weight('commentWeight', 'Trending: a comment counts'),
  weight('registWeight', 'Trending: a re-gist counts'),
  whole('halfLifeHours', 'Trending: a post’s activity counts half after this many hours', 1, 336),
  whole('reportsToHide', 'Reports that hold a post or comment back until the team looks', 1, 50),
  whole('postsPerDay', 'Posts a player can make in a day', 1, 500),
  whole('tagHours', 'Trending in London: count #tags from this many hours', 1, 336),
];

// Gist (as Lagos Life's 📰 Gist): the feed's rules. Part of the content draft, published with it. Reported posts
// and London Gist news are under Players → Gist.
export function GistPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const gist = gistOf(working ?? {});
  const change = (fn: (draft: GistContent) => void) =>
    editor.edit((content) => {
      content.gist ??= structuredClone(gistOf(content));
      fn(content.gist);
    });
  return (
    <section>
      <PageHeader
        title="Gist"
        description="The social feed (as Lagos Life's Gist): how Trending ranks posts, when reports hold a post back, how much a player can post, and how far back Trending in London counts #tags. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">
              <Newspaper size={16} aria-hidden="true" /> The rules
            </h2>
            <label className="designer-check">
              <Switch
                checked={gist.visible}
                aria-label="Gist open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can use Gist
            </label>
            <div className="content-grid">
              {RULES.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`gist-${spec.key}`}
                  spec={spec}
                  value={gist[spec.key] ?? DEFAULT_TAG_HOURS}
                  onChange={(value) => change((draft) => void (draft[spec.key] = value))}
                />
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
